import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { useStableAuth } from '@/hooks/useStableAuth';
import { supabase } from '@/integrations/supabase/client';
import { Upload, Trash2, User } from 'lucide-react';

interface UserAvatarUploadProps {
  currentAvatarUrl?: string | null;
  onAvatarUpdate: (avatarUrl: string | null) => void;
}

export const UserAvatarUpload = ({ currentAvatarUrl, onAvatarUpdate }: UserAvatarUploadProps) => {
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const { toast } = useToast();
  const { userId } = useStableAuth();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!userId || acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Arquivo muito grande",
        description: "O tamanho máximo permitido é 5MB",
        variant: "destructive",
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Tipo de arquivo inválido",
        description: "Apenas imagens são permitidas",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Create file path with user ID
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/avatar.${fileExt}`;

      // Delete old avatar if exists
      if (currentAvatarUrl) {
        const oldPath = currentAvatarUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage
            .from('user-avatars')
            .remove([`${userId}/${oldPath}`]);
        }
      }

      // Upload new avatar
      const { error: uploadError } = await supabase.storage
        .from('user-avatars')
        .upload(fileName, file, {
          upsert: true
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        toast({
          title: "Erro no upload",
          description: "Não foi possível fazer upload da imagem",
          variant: "destructive",
        });
        return;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('user-avatars')
        .getPublicUrl(fileName);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Profile update error:', updateError);
        toast({
          title: "Erro",
          description: "Erro ao atualizar perfil com nova foto",
          variant: "destructive",
        });
        return;
      }

      onAvatarUpdate(publicUrl);
      setImagePreview(null);

      toast({
        title: "Sucesso",
        description: "Foto do perfil atualizada com sucesso!",
        variant: "default",
      });

    } catch (error) {
      console.error('Avatar upload error:', error);
      toast({
        title: "Erro",
        description: "Erro inesperado ao fazer upload da foto",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [userId, currentAvatarUrl, onAvatarUpdate, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: false,
    disabled: loading
  });

  const handleRemoveAvatar = async () => {
    if (!userId || !currentAvatarUrl) return;

    setLoading(true);

    try {
      // Extract file path from URL
      const urlParts = currentAvatarUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const filePath = `${userId}/${fileName}`;

      // Delete from storage
      const { error: deleteError } = await supabase.storage
        .from('user-avatars')
        .remove([filePath]);

      if (deleteError) {
        console.error('Delete error:', deleteError);
      }

      // Update profile to remove avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Profile update error:', updateError);
        toast({
          title: "Erro",
          description: "Erro ao remover foto do perfil",
          variant: "destructive",
        });
        return;
      }

      onAvatarUpdate(null);

      toast({
        title: "Sucesso",
        description: "Foto do perfil removida com sucesso!",
        variant: "default",
      });

    } catch (error) {
      console.error('Remove avatar error:', error);
      toast({
        title: "Erro",
        description: "Erro inesperado ao remover foto",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getUserInitials = () => {
    return 'U'; // Default user initial
  };

  return (
    <div className="space-y-6">
      {/* Current Avatar Display */}
      {currentAvatarUrl && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src={currentAvatarUrl} alt="Avatar atual" />
                <AvatarFallback className="text-2xl">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h4 className="font-medium">Foto Atual</h4>
                <p className="text-sm text-muted-foreground">
                  Esta é sua foto de perfil atual
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveAvatar}
                disabled={loading}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remover
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Area */}
      <Card>
        <CardContent className="p-6">
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${isDragActive 
                ? 'border-primary bg-primary/10' 
                : 'border-border hover:border-primary/50 hover:bg-accent/50'
              }
              ${loading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <input {...getInputProps()} />
            
            <div className="space-y-4">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                {loading ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                ) : (
                  <Upload className="h-8 w-8 text-primary" />
                )}
              </div>
              
              <div>
                <h3 className="text-lg font-medium">
                  {loading 
                    ? 'Fazendo upload...' 
                    : isDragActive 
                      ? 'Solte a imagem aqui' 
                      : currentAvatarUrl
                        ? 'Alterar foto do perfil'
                        : 'Adicionar foto do perfil'
                  }
                </h3>
                
                {!loading && (
                  <p className="text-muted-foreground">
                    Arraste uma imagem ou clique para selecionar
                  </p>
                )}
              </div>
              
              {!loading && (
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Formatos suportados: JPEG, PNG, GIF, WebP</p>
                  <p>Tamanho máximo: 5MB</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {imagePreview && (
        <Card>
          <CardContent className="p-6">
            <h4 className="font-medium mb-3">Prévia</h4>
            <div className="flex items-center space-x-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={imagePreview} alt="Prévia do avatar" />
                <AvatarFallback>
                  <User className="h-8 w-8" />
                </AvatarFallback>
              </Avatar>
              <p className="text-sm text-muted-foreground">
                Assim ficará sua nova foto de perfil
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};